import { useEffect, useRef, useState } from 'preact/hooks';
import {
  PaymentResult,
  PaymentMethodResult,
  WebPaymentMethod,
} from '../iap.types';
import { BackButton, CloseButton } from './Icons';
import { PaymentDetails } from './PaymentDetails';
import { CancelPayment } from './CancelPayment';
import { ProductId } from 'src/replicant/iap';
import { PaymentViewContent } from './PaymentViewContent';
import { PartialRecord } from 'src/replicant/utils/types';
import { now, user } from 'src/StateObserver';
import {
  clearLastSelectedPaymentMethod,
  getLastSelectedPaymentMethod,
  isOpenWebPurchaseMemoryEnabled,
  methodToProviderMapping,
} from '../iap.utils';
import i18n from 'src/lib/i18n/i18n';
import { WebPaymentMethodAnalytics } from '../analytics/analytics.types';
import {
  getKomojuHostedFields,
  getKomojuSessionCache,
  loadKomojuHostedFieldsSDK,
} from '../providers/komoju';
import { PaymentMethodHeader } from './PaymentMethodHeader';
import { useUpdateEffect } from '../hooks/useUpdateEffect';

export function PaymentView({
  productId,
  close,
  setPage,
  onMethodSelected,
  onPurchaseStart,
  onAgeConfirm,
  onCloseStart,
  onCloseCancel,
}: {
  productId: ProductId;
  close: (result: PaymentMethodResult) => void;
  setPage: (page: 'pay' | 'info') => void;
  onMethodSelected: (
    data: WebPaymentMethodAnalytics,
    isCancel: boolean,
  ) => void;
  onPurchaseStart: (data: WebPaymentMethodAnalytics) => void;
  onAgeConfirm: (
    data: WebPaymentMethodAnalytics & { accepted: boolean },
  ) => void;
  onCloseStart: (data: WebPaymentMethodAnalytics) => void;
  onCloseCancel: (data: WebPaymentMethodAnalytics) => void;
}) {
  const [previousSelectedMethod, setPreviousSelectedPaymentMethod] =
    useState<WebPaymentMethod | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<WebPaymentMethod | null>(null);
  const [confirmationData, setConfirmationData] =
    useState<PaymentResult | null>(null);

  // Total number of selects while the popup was open
  const instanceSelectSequence = useRef<number>(0);

  // The timestamp of when the current method was selected
  const methodTimestamp = useRef<number>(0);

  // Counter for selects for each method
  const methodSequence = useRef<PartialRecord<WebPaymentMethod, number>>({});

  const hasAgeCheckbox = useRef<boolean>(false);

  // Triggered when a payment method is selected.
  useUpdateEffect(() => {
    if (selectedPaymentMethod) {
      instanceSelectSequence.current += 1;

      if (methodSequence.current[selectedPaymentMethod]) {
        methodSequence.current[selectedPaymentMethod]! += 1;
      } else {
        methodSequence.current[selectedPaymentMethod] = 1;
      }

      methodTimestamp.current = now();
    }

    // Clear last payment method if none was selected
    if (!selectedPaymentMethod) {
      clearLastSelectedPaymentMethod();
    }

    // One of the two will always be there
    // We're either selecting a method (selectedPaymentMethod is defined)
    // Or deselecting an already selected method (previousSelectedMethod is defined)
    const providerMethod = (selectedPaymentMethod ?? previousSelectedMethod)!;
    hasAgeCheckbox.current =
      selectedPaymentMethod &&
      methodToProviderMapping()[providerMethod] === 'komoju'
        ? !user().ageConsentJPAccepted
        : false;

    onMethodSelected(
      {
        analyticsData: {
          // Include the current select
          instanceSelectSequence: instanceSelectSequence.current,
        },
        methodData: {
          providerMethod,
          instanceSelectSequenceForMethod:
            methodSequence.current[providerMethod] ?? 1,
          methodTimestamp: methodTimestamp.current,
          hasAgeCheckbox: hasAgeCheckbox.current,
        },
      },
      !selectedPaymentMethod,
    );

    setPreviousSelectedPaymentMethod(selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  // Load last selected payment method on mount and auto-select it if available
  useEffect(() => {
    // Load last selected payment method. If available, auto-select it.
    const lastPaymentMethod = getLastSelectedPaymentMethod();
    if (lastPaymentMethod && !selectedPaymentMethod) {
      setSelectedPaymentMethod(lastPaymentMethod as WebPaymentMethod);
    }
  }, []);

  // The parent HTML element for the Komoju fields.
  const komojuFieldsContainer = useRef<HTMLDivElement>();

  // Append the Komoju fields to the form on mount.
  useEffect(() => {
    const fields = getKomojuHostedFields();
    komojuFieldsContainer.current?.appendChild(fields);

    // Set the session id when ready.
    Promise.all([
      loadKomojuHostedFieldsSDK(),
      getKomojuSessionCache(productId).get(),
    ]).then(([_, sessionId]) => {
      fields.setAttribute('session-id', sessionId);
    });

    return () => {
      fields.remove();
    };
  }, []);

  return (
    <>
      {selectedPaymentMethod && (
        <div
          class="back"
          onClick={(e) => {
            e.preventDefault();
            setSelectedPaymentMethod(null);
          }}
        >
          <BackButton />
        </div>
      )}
      <div
        class="close"
        onClick={(e) => {
          e.preventDefault();
          setConfirmationData({
            success: false,
            code: 'CANCELED',
            provider: 'none',
            purchaseID: 'unknown',
            internalTransactionId: 'unknown',
          });
          onCloseStart({
            analyticsData: {
              instanceSelectSequence: instanceSelectSequence.current,
            },
            methodData: {
              providerMethod: selectedPaymentMethod!,
              instanceSelectSequenceForMethod:
                methodSequence.current[selectedPaymentMethod!]!,
              methodTimestamp: methodTimestamp.current,
              hasAgeCheckbox: hasAgeCheckbox.current,
            },
          });
        }}
      >
        <CloseButton />
      </div>
      <PaymentDetails productId={productId} />

      <div class="paymentContent">
        {selectedPaymentMethod && (
          <>
            <PaymentMethodHeader paymentMethod={selectedPaymentMethod} />
            <div class="spacerTop" />
          </>
        )}

        <div
          class="paymentForm"
          id="form"
          // FIXME: Remove ts-ignore.
          // @ts-ignore - Ref is typed wrong.
          ref={komojuFieldsContainer}
        />

        <PaymentViewContent
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          productId={productId}
          close={(paymentResult: PaymentResult) =>
            close({
              paymentResult,
              analyticsData: {
                instanceSelectSequence: instanceSelectSequence.current,
              },
              methodData: {
                providerMethod: selectedPaymentMethod!,
                instanceSelectSequenceForMethod:
                  methodSequence.current[selectedPaymentMethod!]!,
                methodTimestamp: methodTimestamp.current,
                hasAgeCheckbox: hasAgeCheckbox.current,
              },
            })
          }
          onPurchaseStart={() => {
            onPurchaseStart({
              analyticsData: {
                instanceSelectSequence: instanceSelectSequence.current,
              },
              methodData: {
                providerMethod: selectedPaymentMethod!,
                instanceSelectSequenceForMethod:
                  methodSequence.current[selectedPaymentMethod!]!,
                methodTimestamp: methodTimestamp.current,
                hasAgeCheckbox: hasAgeCheckbox.current,
              },
            });
          }}
          onAgeConfirm={(accepted: boolean) => {
            onAgeConfirm({
              analyticsData: {
                instanceSelectSequence: instanceSelectSequence.current,
              },
              methodData: {
                providerMethod: selectedPaymentMethod!,
                instanceSelectSequenceForMethod:
                  methodSequence.current[selectedPaymentMethod!]!,
                methodTimestamp: methodTimestamp.current,
                hasAgeCheckbox: hasAgeCheckbox.current,
              },
              accepted,
            });
          }}
        />

        {selectedPaymentMethod && <div class="spacerBottom" />}
      </div>

      <CancelPayment
        visible={confirmationData !== null}
        onClose={(confirm) => {
          const data = confirmationData;
          setConfirmationData(null);
          if (!confirm) {
            onCloseCancel({
              analyticsData: {
                instanceSelectSequence: instanceSelectSequence.current,
              },
              methodData: {
                providerMethod: selectedPaymentMethod!,
                instanceSelectSequenceForMethod:
                  methodSequence.current[selectedPaymentMethod!]!,
                methodTimestamp: methodTimestamp.current,
                hasAgeCheckbox: hasAgeCheckbox.current,
              },
            });
            return;
          }
          close({
            paymentResult: data!,
            analyticsData: {
              instanceSelectSequence: instanceSelectSequence.current,
            },
            methodData: selectedPaymentMethod
              ? {
                  providerMethod: selectedPaymentMethod,
                  instanceSelectSequenceForMethod:
                    methodSequence.current[selectedPaymentMethod]!,
                  methodTimestamp: methodTimestamp.current,
                  hasAgeCheckbox: hasAgeCheckbox.current,
                }
              : undefined,
          });
        }}
      />

      {isOpenWebPurchaseMemoryEnabled() && selectedPaymentMethod && (
        <div
          class="chooseDifferentMethod"
          onClick={(e) => {
            e.preventDefault();
            setSelectedPaymentMethod(null);
          }}
        >
          {i18n('openWebPay.chooseDifferentMethod')}
        </div>
      )}
    </>
  );
}
